import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import NumericInput from "../components/NumericInput";
import RadioButtonGroup from "../components/RadioButtonGroup";
import CheckBoxGroup from "../components/CheckBoxGroup";
import DropdownSelect from "../components/DropdownSelect";
import age from "../data/age";
import gender from "../data/gender";
import preference from "../data/preference";
import frequency from "../data/frequency";
import duration from "../data/duration";
import sportList from "../data/sportList";
import year from "../data/year";
import skillLevel from "../data/skillLevel";
import trainingTypes from "../data/trainingTypes";
import fitnessGoals from "../data/fitnessGoals";
import intensity from "../data/intensity";
import time from "../data/time";
import season from "../data/season";
import { getNextUserId, saveUser, saveRecommendations } from "../tinyDB";

const FormPage = () => {
  const radioValues = useSelector((state) => state.radio.selectedValues);
  const numberValues = useSelector((state) => state.number.values);
  const checkboxValues = useSelector((state) => state.checkbox.selectedValues);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(true);
  const [errorFields, setErrorFields] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    setErrorFields((prevErrors) => {
      let updatedErrors = { ...prevErrors };
  
      Object.keys(updatedErrors).forEach((key) => {
        if (
          (radioValues[key] && radioValues[key] !== "") ||
          (numberValues[key] && numberValues[key] !== "") ||
          (checkboxValues[key] && checkboxValues[key].length > 0)
        ) {
          delete updatedErrors[key];
        }
      });

      const optionLists = {
        fitnessGoals,
        preferredTrainingTypes: trainingTypes,
        // historySportList: sportList,
        // favouriteSportList: sportList,
      };
  
      Object.keys(checkboxValues).forEach((key) => {
        const selectedValues = checkboxValues[key] || [];
  
        if (!optionLists[key]) return;
  
        const maxPredefinedValue = Math.max(...optionLists[key].map(opt => opt.value));
        const hasOther = selectedValues.some(value => value > maxPredefinedValue);
  
        if (hasOther) {
          const customField = document.querySelector(`[data-field="${key}-custom"]`);
          if (customField && customField.value.trim()) {
            delete updatedErrors[key];
          } else {
            updatedErrors[key] = true;
          }
        }
      });
  
      return updatedErrors;
    });
  }, [radioValues, numberValues, checkboxValues]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      setShowScrollTop(scrollTop > 50);
      setShowScrollBottom(scrollTop + clientHeight < scrollHeight - 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToBottom = () => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });

  const handleSubmit = async() => {
    let newErrors = {};
    let otherLabels = {};
  
    const optionLists = {
      fitnessGoals,
      preferredTrainingTypes: trainingTypes,
      // historySportList: sportList,
      // favouriteSportList: sportList,
    };
    
    if (!radioValues.age) newErrors.age = true;
    if (!numberValues.weight) newErrors.weight = true;
    if (!numberValues.height) newErrors.height = true;
    if (!radioValues.gender) newErrors.gender = true;
    if (!radioValues.preferences) newErrors.preferences = true;
    if (!checkboxValues.fitnessGoals || checkboxValues.fitnessGoals.length === 0) newErrors.fitnessGoals = true;
    if (!radioValues.frequency) newErrors.frequency = true;
    if (!radioValues.duration) newErrors.duration = true;
    if (!checkboxValues.preferredTrainingTypes || checkboxValues.preferredTrainingTypes.length === 0) newErrors.preferredTrainingTypes = true;
    if (!checkboxValues.intensityLevel || checkboxValues.intensityLevel.length === 0) newErrors.intensityLevel = true;
    if (!checkboxValues.timePreferences || checkboxValues.timePreferences.length === 0) newErrors.timePreferences = true;
    if (!checkboxValues.seasonalPreferences || checkboxValues.seasonalPreferences.length === 0) newErrors.seasonalPreferences = true;
    // if (!checkboxValues.historySportList || checkboxValues.historySportList.length === 0) newErrors.historySportList = true;
    // if (!checkboxValues.favouriteSportList || checkboxValues.favouriteSportList.length === 0) newErrors.favouriteSportList = true;
    if (!radioValues.bestSport) newErrors.bestSport = true;
    if (!radioValues.year) newErrors.year = true;
    if (!radioValues.skillLevel) newErrors.skillLevel = true;
  
    Object.keys(checkboxValues).forEach((key) => {
      const selectedValues = checkboxValues[key] || [];

      if (!optionLists[key]) return;

      const maxPredefinedValue = Math.max(...optionLists[key].map(opt => opt.value));

      const hasOther = selectedValues.some(value => value > maxPredefinedValue);

      if (hasOther) {
        const customField = document.querySelector(`[data-field="${key}-custom"]`);
        if (customField) {
          const inputValue = customField.value.trim();
  
          if (!inputValue) {
            newErrors[key] = true;
          } else {
            otherLabels[key] = inputValue;
          }
        }
      }
    });
  
    setErrorFields(newErrors);
  
    if (Object.keys(newErrors).length > 0) {
      const firstMissingField = document.querySelector(`[data-field="${Object.keys(newErrors)[0]}"]`);
      if (firstMissingField) {
        firstMissingField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setLoading(true);
    try {
      const user_id = await getNextUserId();

      console.log("Generated user_id:", user_id);

      await saveUser(user_id, radioValues, numberValues, checkboxValues, otherLabels);

      // const API_URL = "https://api.sportsrecsys.xyz/recommend";
      const API_URL = "http://127.0.0.1:5000/recommend";
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          ...radioValues,
          ...numberValues,
          ...checkboxValues,
          otherLabels,
        }),
      });
  
      const data = await response.json();

      if (data.recommended_items?.length > 0 && data.item_details?.length > 0 && user_id) {
        const recommendationWithLabels = data.recommended_items.map(itemId => {
          const item = data.item_details.find(detail => detail.item_id === itemId);
          return {
            [itemId]: item ? item.Name : "Unknown"
          };
        });
        await saveRecommendations(user_id, recommendationWithLabels);
        navigate("/recommendations", { state: { recommendations: data.recommended_items, itemDetails: data.item_details, user_id: user_id } });
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page-container">
      {loading && (
        <div className="loading-overlay">
          <div className="spinner-border text-light" role="status" style={{ width: "4rem", height: "4rem" }}></div>
          <p className="mt-3">Processing your request...</p>
        </div>
      )}
      <div className="container form-title">
        <div className="p-4 text-center text-white rounded" style={{ backgroundColor: "#4da6ff" }}>
          <h2 className="fw-bold">Sports Recommendation System</h2>
        </div>
      </div>
      <div className="container mt-3">
        <div className="row">
          <div className="col-md-6 col-sm-12">
            <RadioButtonGroup label="Age" options={age} stateName="age" error={!!errorFields.age} maxPerRow={2} />
          </div>

          <div className="col-md-6 col-sm-12">
            <RadioButtonGroup label="Gender" options={gender} stateName="gender" error={!!errorFields.gender} />
          </div>

          <div className="col-md-6 col-sm-12">
            <NumericInput label="Weight (kg)" stateName="weight" error={!!errorFields.weight}/>
          </div>
          
          <div className="col-md-6 col-sm-12">
            <NumericInput label="Height (cm)" stateName="height" error={!!errorFields.height}/>
          </div>
          
          <div className="col-md-6">
            <RadioButtonGroup
              label="Prefer Exercises or Sports"
              options={preference}
              stateName="preferences"
              description="Please select the option that best describes your preference:
              <ul style='margin-top: 8px;'>
                <li><strong>Exercises:</strong> Includes activities like <strong><em>weightlifting, yoga, stretching, and bodyweight workouts</em></strong> that focus on improving strength, flexibility, and overall fitness.</li>
                <li><strong>Sports:</strong> Organized activities such as <strong><em>soccer, basketball, and tennis</em></strong>, played in teams or individually with competitive rules.</li>
                <li><strong>Both:</strong> You enjoy both exercises and sports equally and participate in both types of activities.</li>
              </ul>"
              error={!!errorFields.preferences}
            />
          </div>

          <div className="col-md-6">
            <CheckBoxGroup
              label="Fitness Goals"
              options={fitnessGoals}
              stateName="fitnessGoals"
              initialDisplayCount={5}
              customTopOptions={[17, 9, 5, 6, 7]}
              allowOther={true}
              errorProp={!!errorFields.fitnessGoals}
              enableSearch={true}
            />
          </div>

          <div className="col-md-6 col-sm-12">
            <RadioButtonGroup label="How many times per week do you exercise?" options={frequency} stateName="frequency" error={!!errorFields.frequency}/>
          </div>

          <div className="col-md-6 col-sm-12">
            <RadioButtonGroup label="Average duration per session (in minutes)" options={duration} stateName="duration" error={!!errorFields.duration}/>
          </div>

          <div className="col-md-6 col-sm-12">
            <CheckBoxGroup
              label="Preferred Training Types"
              options={trainingTypes}
              stateName="preferredTrainingTypes"
              initialDisplayCount={4}
              customTopOptions={[13, 5, 6, 9]}
              allowOther={true}
              errorProp={!!errorFields.preferredTrainingTypes}
              enableSearch={true}
            />
          </div>

          <div className="col-md-6 col-sm-12">
            <CheckBoxGroup
              label="Intensity Level Preferred for Sports / Exercises"
              options={intensity}
              stateName="intensityLevel"
              description="<ul style='margin-top: 8px;'>
                <li><strong>Light:</strong> Activities where you can <strong><em>comfortably hold a conversation</em></strong> (e.g., gentle stretching, slow walking).</li>
                <li><strong>Moderate:</strong> Activities where you <strong><em>can talk but not sing comfortably</em></strong> (e.g., brisk walking, moderate cycling).</li>
                <li><strong>High:</strong> Activities where <strong><em>talking is difficult</em></strong> (e.g., running, intense aerobics).</li>
                <li><strong>Very High:</strong> Activities that <strong><em>push your limits</em></strong> and require short bursts of <strong><em>maximum effort</em></strong> (e.g., sprinting, HIIT).</li>
              </ul>"
              errorProp={!!errorFields.intensityLevel}
            />
          </div>

          <div className="col-md-6 col-sm-12">
            <CheckBoxGroup
              label="Time Preferences"
              options={time}
              stateName="timePreferences"
              errorProp={!!errorFields.timePreferences}
            />
          </div>
          
          <div className="col-md-6 col-sm-12">
            <CheckBoxGroup
              label="Seasonal Preferences"
              options={season}
              stateName="seasonalPreferences"
              errorProp={!!errorFields.seasonalPreferences}
            />
          </div>

          {/* <div className="col-md-6 col-sm-12">
            <CheckBoxGroup
              label="Exercises / Sports History"
              tooltip="Activities you did in the past but no longer practice."
              options={sportList}
              stateName="historySportList"
              initialDisplayCount={16}
              customTopOptions={[45, 59, 68, 24, 8, 69, 60, 6, 53, 64, 3, 66, 56, 37, 39, 23]}
              allowOther={true}
              enableSearch={true}
              errorProp={!!errorFields.historySportList}
            />
          </div>

          <div className="col-md-6 col-sm-12">
            <CheckBoxGroup
              label="Favourite Sports / Exercises"
              tooltip="Activities you currently enjoy and actively participate in."
              options={sportList}
              stateName="favouriteSportList"
              initialDisplayCount={16}
              customTopOptions={[45, 59, 68, 24, 8, 69, 60, 6, 53, 64, 3, 66, 56, 37, 39, 23]}
              allowOther={true}
              enableSearch={true}
              errorProp={!!errorFields.favouriteSportList}
            />
          </div> */}

          <fieldset className="form-section mt-3">
            <legend className="d-flex fw-bold justify-content-center">
              Best Sport / Exercise & Skill Level
            </legend>          
            
            <div className="row">
              <div className="col-md-4 col-sm-8">
                <DropdownSelect
                  label="Select the sport or exercise you <strong>excel at</strong> or <strong>enjoy most</strong>."
                  options={sportList}
                  stateName="bestSport"
                  searchable={true}
                  error={!!errorFields.bestSport}
                />
              </div>

              <div className="col-md-4 col-sm-8">
                <DropdownSelect
                  label="How many <strong>years</strong> have you practiced?"
                  options={year}
                  stateName="year"
                  error={!!errorFields.year}
                />
              </div>

              <div className="col-md-4 col-sm-8">
                <DropdownSelect
                  label="How would you rate your <strong>skill level</strong>?"
                  tooltip="The skill level is totally up to your own judgement."
                  options={skillLevel}
                  stateName="skillLevel"
                  error={!!errorFields.skillLevel}
                />
              </div>
            </div>
          </fieldset>


          <div className="col-12 mt-3 text-center">
            <button
              className="container btn btn-primary mt-3 mb-3"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>

        </div>
        <div className="position-fixed end-0 bottom-0 p-3 d-flex flex-column gap-2" style={{ right: "20px", bottom: "20px" }}>
          {showScrollTop && (
            <button className="btn btn-light rounded-circle p-2 shadow-sm scroll-btn" onClick={scrollToTop} style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-arrow-up" style={{ fontSize: "1.5rem", color: "#555" }}></i>
            </button>
          )}
          {showScrollBottom && (
            <button className="btn btn-light rounded-circle p-2 shadow-sm scroll-btn" onClick={scrollToBottom} style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-arrow-down" style={{ fontSize: "1.5rem", color: "#555" }}></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormPage;
